import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useInView } from '../../../hooks/useInView';

const ProductCategories = () => {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  const categories = [
    {
      name: 'Steel Building Packages',
      path: '/products/steel-building',
      description: 'Pre-engineered steel structures for warehouses, manufacturing facilities, and commercial buildings',
      whatIncluded: 'Complete structural steel, fasteners, roof/wall panels, doors, windows, engineering drawings',
      idealFor: 'Developers, industrial contractors, warehouse builders',
      savings: 'Save 25-35%',
      image: 'https://public.readdy.ai/ai/img_res/ce011306e43a37b2c23934310b87c9d7.jpg',
    },
    {
      name: 'Kitchens & Cabinets',
      path: '/products/kitchens',
      description: 'Custom cabinetry, countertops, and complete kitchen packages for residential and commercial projects',
      whatIncluded: 'Custom cabinets, countertops, hardware, sinks, faucets, installation instructions',
      idealFor: 'Home builders, condo developers, renovation contractors, property flippers',
      savings: 'Save 20-35%',
      image: 'https://readdy.ai/api/search-image?query=modern%20luxury%20kitchen%20interior%20with%20premium%20white%20cabinetry%20sleek%20countertops%20and%20professional%20grade%20appliances%20in%20bright%20minimalist%20setting%20with%20clean%20lines%20and%20sophisticated%20design&width=600&height=400&seq=cat-kitchen-002&orientation=landscape',
    },
    {
      name: 'Flooring & Tile',
      path: '/products/flooring',
      description: 'Hardwood, luxury vinyl, tile, and commercial flooring systems for large-scale installations',
      whatIncluded: 'Hardwood, LVP, tile, underlayment, trim, adhesives, installation materials',
      idealFor: 'Multi-unit developers, commercial contractors, custom builders',
      savings: 'Save 25-40%',
      image: 'https://readdy.ai/api/search-image?query=elegant%20hardwood%20flooring%20installation%20in%20modern%20commercial%20space%20with%20natural%20wood%20grain%20texture%20and%20professional%20finish%20in%20bright%20neutral%20setting&width=600&height=400&seq=cat-flooring-002&orientation=landscape',
    },
    {
      name: 'Windows & Doors',
      path: '/products/windows-doors',
      description: 'Energy-efficient windows, entry systems, and commercial-grade doors for multi-unit projects',
      whatIncluded: 'Vinyl/aluminum windows, entry doors, patio doors, commercial doors, hardware, installation guides',
      idealFor: 'Multi-family developers, custom home builders, renovation contractors',
      savings: 'Save 30-40%',
      image: 'https://readdy.ai/api/search-image?query=modern%20architectural%20windows%20and%20glass%20doors%20in%20contemporary%20building%20with%20sleek%20aluminum%20frames%20and%20energy%20efficient%20glazing%20in%20bright%20minimalist%20setting&width=600&height=400&seq=cat-windows-002&orientation=landscape',
    },
    {
      name: 'Exterior Finishes',
      path: '/products/exteriors',
      description: 'Siding, roofing, cladding, trim, and complete exterior finishing materials for construction projects',
      whatIncluded: 'Siding, roofing materials, cladding panels, insulation, trim, fasteners',
      idealFor: 'General contractors, developers, renovation specialists',
      savings: 'Save 20-30%',
      image: 'https://readdy.ai/api/search-image?query=modern%20building%20exterior%20with%20premium%20architectural%20cladding%20siding%20materials%20contemporary%20design%20clean%20lines%20professional%20finish%20neutral%20tones&width=600&height=400&seq=cat-exterior-003&orientation=landscape',
    },
    {
      name: 'Hardware & Fasteners',
      path: '/products/hardware',
      description: 'Structural hardware, fasteners, anchors, and construction fittings for large-scale projects',
      whatIncluded: 'Bolts, anchors, brackets, hinges, locks, structural connectors, specialty fasteners',
      idealFor: 'General contractors, structural engineers, large-scale builders',
      savings: 'Save 30-45%',
      image: 'https://readdy.ai/api/search-image?query=professional%20construction%20hardware%20fasteners%20bolts%20anchors%20brackets%20and%20structural%20fittings%20neatly%20arranged%20on%20clean%20industrial%20surface%20with%20metallic%20finish&width=600&height=400&seq=cat-hardware-001&orientation=landscape',
    },
    {
      name: 'Lighting Systems',
      path: '/products/lighting',
      description: 'Commercial and residential lighting packages including LED systems, fixtures, and smart controls',
      whatIncluded: 'LED fixtures, pendant lights, recessed lighting, outdoor lighting, smart controls, wiring accessories',
      idealFor: 'Hotel developers, condo builders, commercial fit-out contractors',
      savings: 'Save 25-40%',
      image: 'https://readdy.ai/api/search-image?query=modern%20commercial%20LED%20lighting%20fixtures%20installed%20in%20contemporary%20interior%20space%20with%20warm%20ambient%20glow%20sleek%20pendant%20lights%20and%20recessed%20ceiling%20lights%20in%20minimalist%20setting&width=600&height=400&seq=cat-lighting-001&orientation=landscape',
    },
    {
      name: 'Plumbing & Fixtures',
      path: '/products/plumbing',
      description: 'Complete plumbing packages including pipes, fittings, fixtures, and bathroom suites for bulk projects',
      whatIncluded: 'Pipes, fittings, valves, toilets, sinks, showers, bathtubs, faucets, water heaters',
      idealFor: 'Multi-family developers, hotel builders, commercial contractors',
      savings: 'Save 25-35%',
      image: 'https://readdy.ai/api/search-image?query=modern%20bathroom%20plumbing%20fixtures%20including%20sleek%20faucets%20sinks%20toilets%20and%20shower%20systems%20in%20contemporary%20minimalist%20bathroom%20with%20clean%20white%20surfaces%20and%20chrome%20finishes&width=600&height=400&seq=cat-plumbing-001&orientation=landscape',
    },
    {
      name: 'Furniture Packages',
      path: '/products/furniture',
      description: 'Commercial and residential furniture for hotels, multi-family units, and model homes',
      whatIncluded: 'Bedroom sets, living room furniture, dining sets, office furniture, custom upholstery',
      idealFor: 'Hotel developers, multi-family investors, property managers, builders',
      savings: 'Save 30-45%',
      image: 'https://readdy.ai/api/search-image?query=modern%20commercial%20furniture%20set%20with%20sleek%20chairs%20and%20tables%20in%20contemporary%20office%20space%20with%20minimalist%20design%20and%20professional%20finish&width=600&height=400&seq=cat-furniture-002&orientation=landscape',
    },
  ];

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-display font-light text-4xl md:text-5xl text-gray-900 mb-4">
            Direct Import <span className="font-semibold">Product Categories</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            From steel structures to complete interior packages — we source everything your project needs directly from verified global manufacturers
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {categories.map((category, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.08, duration: 0.6 }}
              className="group bg-gray-50 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-200"
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={category.image}
                  alt={category.name}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 right-4 bg-primary-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  {category.savings}
                </div>
              </div>
              
              <div className="p-8">
                <h3 className="font-display font-semibold text-2xl text-gray-900 mb-3">
                  {category.name}
                </h3>
                <p className="text-base text-gray-700 mb-6 leading-relaxed">
                  {category.description}
                </p>

                <div className="space-y-4 mb-6">
                  <div className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-secondary-600 text-xl"></i>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-semibold text-gray-900 mb-1">What's Included:</div>
                      <div className="text-sm text-gray-600">{category.whatIncluded}</div>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-user-line text-secondary-600 text-xl"></i>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-semibold text-gray-900 mb-1">Ideal For:</div>
                      <div className="text-sm text-gray-600">{category.idealFor}</div>
                    </div>
                  </div>
                </div>

                <Link
                  to={category.path}
                  className="block w-full text-center px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-base font-semibold rounded-lg hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
                >
                  View Details & Get Quote
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductCategories;
